import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextControl } from '@/module/Author/Text/TextControl';
import { BookMembershipSelectControl } from '@/module/Nomen/BookMembership/BookMembershipSelectControl';
import { TranslationMembershipSelectControl } from '@/module/Nomen/TranslationMembership/TranslationMembershipSelectControl';
import { TranslationTypeSelectControl } from '@/module/Nomen/TranslationType/TranslationTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { OriginalBookAuthorListControl } from './components/OriginalBookAuthorListControl';
import { TranslationSchema } from './model/TranslationSchema';

interface TranslationFormProps extends FormSubmit<TranslationSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function TranslationForm(props: TranslationFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showScopusFields, showWosFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<TranslationSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<TranslationSchema>
              name="bookName"
              label={t('translations.form.fields.bookName')}
              isSubmitting={isSubmitting}
            />
            <TranslationTypeSelectControl<TranslationSchema>
              label={t('translations.form.fields.translationType')}
              name="type"
              isSubmitting={isSubmitting}
            />
            <TextControl
              isSubmitting={isSubmitting}
              showWosFields={showWosFields}
              showScopusFields={showScopusFields}
            />
          </Content>
          <Content type="full">
            <OriginalBookAuthorListControl<TranslationSchema>
              name="bookAuthors"
              isSubmitting={isSubmitting}
              label={t('chapters.form.fields.bookAuthors')}
              renderMembership={({ membershipName }) => (
                <BookMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<TranslationSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <TranslationMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
